import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, Puzzle, Coins, Gift, Ticket, Lock, Sparkles, Newspaper, BellRing, CheckCircle2, Bookmark, Trophy, CloudSun, Crown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { getCouponsPreview } from "@/lib/api";
import { storeName, setAfterAuth } from "@/components/CouponOfDay";
import { rememberSignupCta } from "@/lib/membership";
import { ExpiryBadge } from "@/components/CouponDetails";

const SIGNUP = "/registrati";
const COUPONS = "/coupon";
const useMember = () => !!useAuth().user;
export const Wrap = ({ children, className = "", id, testid }) => (
  <section id={id} data-testid={testid} className={`max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-8 ${className}`}>{children}</section>
);
const Kicker = ({ children }) => (
  <p className="font-mono text-[11px] font-bold uppercase tracking-wider text-brand mb-3">{children}</p>
);
const H2 = ({ children }) => (
  <h2 className="font-serif text-3xl sm:text-4xl font-semibold leading-tight text-onSurface">{children}</h2>
);

export function SignupCta({ testid, label = "Iscriviti gratis", className = "" }) {
  const member = useMember();
  return (
    <Link to={member ? COUPONS : SIGNUP} data-testid={testid} className={`group inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-brand text-onBrand text-sm font-bold uppercase tracking-wide hover:bg-brandSecondary transition-colors ${className}`}>
      {member ? "Vai ai tuoi coupon" : label} <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
    </Link>
  );
}

function MemberNote() {
  const { user } = useAuth();
  if (!user) return null;
  return (
    <div data-testid="vantaggi-member-note" className="mt-7 flex items-start gap-3 rounded-2xl border border-brand/30 bg-brandTertiary/30 p-4 max-w-2xl">
      <CheckCircle2 size={22} className="text-brand shrink-0 mt-0.5" />
      <div>
        <p className="font-serif text-lg font-semibold text-onSurface">{user.first_name ? `Ciao ${user.first_name}, sei già iscritto!` : "Sei già iscritto!"}</p>
        <p className="text-sm text-onSurfaceSecondary mt-0.5">Questo è il tuo Readoo: interessi, salvati e vantaggi ti aspettano nel profilo.</p>
      </div>
    </div>
  );
}

export function Hero() {
  const member = useMember();
  return (
    <Wrap testid="vantaggi-hero" className="pt-10 sm:pt-16 pb-10">
      <Kicker>Readoo è gratuito per tutti</Kicker>
      <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.02] text-onSurface max-w-3xl">Registrati. Readoo diventa tuo.</h1>
      <p data-testid="vantaggi-hero-claim" className="mt-5 text-lg sm:text-xl leading-relaxed text-onSurfaceSecondary max-w-2xl">
        Personalizza le notizie, salva quello che ti interessa, accedi ai vantaggi e sarai tra i primi a scoprire le prossime novità Readoo.
      </p>
      <MemberNote />
      <div className="mt-7 flex flex-col sm:flex-row gap-3">
        {member ? <Link to="/profilo" data-testid="vantaggi-hero-cta" className="group inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-brand text-onBrand text-sm font-bold uppercase tracking-wide hover:bg-brandSecondary transition-colors">Vai al tuo Readoo <ArrowRight size={16} /></Link>
          : <SignupCta testid="vantaggi-hero-cta" label="Registrati gratis" />}
      </div>
      {!member && <p data-testid="vantaggi-hero-note" className="mt-4 text-sm text-subtle">Gratis. Nessun abbonamento richiesto.</p>}
    </Wrap>
  );
}

const PERKS = [
  { icon: Newspaper, t: "Il tuo feed Per te", d: "Scegli gli argomenti che ami e trova un Readoo costruito sui tuoi interessi." },
  { icon: Bookmark, t: "Salva le notizie", d: "Metti da parte gli articoli che vuoi leggere con calma." },
  { icon: Trophy, t: "Il tuo calcio", d: "Segui le tue squadre preferite." },
  { icon: CloudSun, t: "Meteo e oroscopo personali", d: "Ritrova automaticamente la tua città e il tuo segno ogni volta che accedi." },
  { icon: Ticket, t: "Coupon e vantaggi", d: "Accedi alla selezione di codici sconto e offerte già disponibili su Readoo." },
  { icon: Crown, t: "Founder Readoo", d: "Entra nella prima community e accedi in anticipo alle prossime novità." },
  { icon: Sparkles, t: "Readoo Punti", d: "Il programma è in arrivo. Gli utenti registrati saranno tra i primi a scoprirlo.", soon: true },
];

export function MoreThanAccount() {
  return (
    <Wrap testid="vantaggi-perks" className="py-14 sm:py-20">
      <Kicker>Perché registrarsi</Kicker>
      <H2>Più di un semplice account.</H2>
      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {PERKS.map((c, i) => (
          <div key={c.t} data-testid={`vantaggi-perk-${i + 1}`} className="rounded-2xl border border-hair bg-surfaceSecondary p-5">
            <div className="flex items-center justify-between">
              <span className="w-10 h-10 rounded-full bg-brand/12 text-brand flex items-center justify-center"><c.icon size={19} /></span>
              {c.soon && <span className="px-2.5 py-1 rounded-full bg-brand/15 text-brand font-mono text-[10px] font-bold uppercase tracking-wider">In arrivo</span>}
            </div>
            <h3 className="mt-4 font-serif text-xl font-semibold text-onSurface">{c.t}</h3>
            <p className="mt-1.5 text-sm leading-relaxed text-onSurfaceSecondary">{c.d}</p>
          </div>
        ))}
      </div>
    </Wrap>
  );
}

export function AlreadyAvailable() {
  const member = useMember();
  const guest = () => { rememberSignupCta("vantaggi"); setAfterAuth(COUPONS); };
  return (
    <div data-testid="vantaggi-available" className="bg-surfaceSecondary border-y border-hair">
      <Wrap className="py-12 sm:py-16 flex flex-col sm:flex-row sm:items-center justify-between gap-6">
        <div>
          <H2>Vantaggi già disponibili</H2>
          <p className="mt-3 text-base sm:text-[17px] text-onSurfaceSecondary">Essere parte della community Readoo conviene già oggi.</p>
        </div>
        <Link to={member ? COUPONS : SIGNUP} onClick={member ? undefined : guest} data-testid="vantaggi-available-cta" className="group shrink-0 inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-brand text-onBrand text-sm font-bold uppercase tracking-wide hover:bg-brandSecondary transition-colors">
          Scopri coupon e offerte <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
        </Link>
      </Wrap>
    </div>
  );
}

export function Band() {
  const parts = ["Oggi vantaggi.", "Domani Readoo Punti.", "Sempre informazione che ti premia."];
  return (
    <div data-testid="vantaggi-band" className="bg-brand text-onBrand">
      <Wrap className="py-5 sm:py-6">
        <p className="font-serif text-xl sm:text-2xl font-semibold leading-snug flex flex-col sm:flex-row sm:flex-wrap sm:gap-x-3">
          {parts.map((p, i) => <span key={p} className={i === 2 ? "opacity-85" : ""}>{p}</span>)}
        </p>
      </Wrap>
    </div>
  );
}

function OfferCard({ o }) {
  const member = useMember();
  return (
    <Link to={member ? COUPONS : SIGNUP} data-testid={`vantaggi-offer-${o.promotion_id}`} className="group flex flex-col rounded-2xl border border-hair bg-surfaceSecondary p-5 hover:border-brand/50 transition-colors">
      <p className="font-mono text-[11px] font-bold uppercase tracking-wider text-brand">{storeName(o.advertiser_name)}</p>
      <h3 className="mt-1.5 font-serif text-lg font-semibold leading-snug text-onSurface line-clamp-2">{o.title}</h3>
      <div className="mt-auto pt-4 flex flex-wrap items-center justify-between gap-2">
        <ExpiryBadge end={o.end_date} id={`vantaggi-offer-${o.promotion_id}-expiry`} />
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-subtle group-hover:text-brand transition-colors">
          {member ? <><Ticket size={13} /> Vedi l’offerta</> : <><Lock size={13} /> {o.has_code ? "Codice per gli iscritti" : "Riservato agli iscritti"}</>}
        </span>
      </div>
    </Link>
  );
}

export function TodayPerks() {
  const [data, setData] = useState(null);
  useEffect(() => { getCouponsPreview().then(setData).catch(() => setData({ offers: [] })); }, []);
  const offers = data?.offers || [];
  return (
    <Wrap id="vantaggi-oggi" testid="vantaggi-today" className="py-14 sm:py-20 scroll-mt-24">
      <Kicker>Oggi</Kicker>
      <H2>I vantaggi Readoo iniziano da subito.</H2>
      <p className="mt-4 text-base sm:text-[17px] leading-relaxed text-onSurfaceSecondary max-w-2xl">Registrandoti a Readoo puoi accedere a una selezione di buoni sconto e vantaggi Premium riservati alla nostra community.</p>
      <div data-testid="vantaggi-premium-card" className="mt-8 relative overflow-hidden rounded-2xl border border-brand/30 bg-brandTertiary/30 p-6 sm:p-8">
        <span aria-hidden className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-brand/10" />
        <div className="relative flex items-start gap-4">
          <span className="shrink-0 w-12 h-12 rounded-full bg-brand text-onBrand flex items-center justify-center"><Ticket size={22} /></span>
          <div>
            <h3 className="font-serif text-2xl font-semibold text-onSurface">Vantaggi Premium</h3>
            <p className="mt-1.5 text-base text-onSurfaceSecondary">Offerte, sconti e opportunità dedicate agli utenti registrati Readoo.</p>
          </div>
        </div>
      </div>
      {offers.length > 0 && (
        <>
          <p data-testid="vantaggi-offers-label" className="mt-10 mb-4 font-mono text-[11px] font-bold uppercase tracking-wider text-subtle">Alcuni vantaggi disponibili ora</p>
          <div data-testid="vantaggi-offers-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {offers.map((o) => <OfferCard key={o.promotion_id} o={o} />)}
          </div>
        </>
      )}
    </Wrap>
  );
}

export function PointsIntro() {
  return (
    <div data-testid="vantaggi-points" className="bg-surfaceSecondary border-y border-hair">
      <Wrap className="py-14 sm:py-20">
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-brand/15 text-brand font-mono text-[10px] font-bold uppercase tracking-wider mb-4"><Sparkles size={12} /> Prossimamente</span>
        <H2>Arrivano i Readoo Punti</H2>
        <div className="mt-4 space-y-3 text-base sm:text-[17px] leading-relaxed text-onSurfaceSecondary max-w-2xl">
          <p>Stiamo trasformando Readoo in una nuova esperienza di informazione e partecipazione.</p>
          <p>Leggere le notizie, scoprire nuovi contenuti e completare attività e sfide ti permetterà di accumulare Readoo Punti.</p>
        </div>
      </Wrap>
    </div>
  );
}

const HOW = [
  { icon: BookOpen, t: "Leggi", d: "Segui le notizie e scopri ogni giorno nuovi contenuti." },
  { icon: Puzzle, t: "Partecipa", d: "Completa attività, quiz e sfide proposte da Readoo." },
  { icon: Coins, t: "Accumula Readoo Punti", d: "Le tue attività potranno trasformarsi in Readoo Punti." },
  { icon: Gift, t: "Ottieni premi", d: "Utilizza i Readoo Punti per accedere a premi e vantaggi." },
];

export function HowItWorks() {
  return (
    <Wrap testid="vantaggi-how" className="py-14 sm:py-20">
      <Kicker>Come funzionerà</Kicker>
      <H2>Più partecipi, più Readoo ti premia.</H2>
      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {HOW.map((c, i) => (
          <div key={c.t} data-testid={`vantaggi-how-card-${i + 1}`} className="rounded-2xl border border-hair bg-surfaceSecondary p-5">
            <div className="flex items-center justify-between">
              <span className="w-10 h-10 rounded-full bg-brand/12 text-brand flex items-center justify-center"><c.icon size={19} /></span>
              <span className="font-mono text-xs font-bold text-subtle">0{i + 1}</span>
            </div>
            <h3 className="mt-4 font-serif text-xl font-semibold text-onSurface">{c.t}</h3>
            <p className="mt-1.5 text-sm leading-relaxed text-onSurfaceSecondary">{c.d}</p>
          </div>
        ))}
      </div>
      <p data-testid="vantaggi-how-note" className="mt-5 text-sm text-subtle">Readoo Punti è in fase di sviluppo: modalità e premi saranno comunicati al lancio.</p>
    </Wrap>
  );
}

const WHY = [
  { icon: Ticket, t: "Vantaggi subito", d: "Accedi ai vantaggi Premium già disponibili per gli utenti registrati." },
  { icon: BellRing, t: "Readoo Punti in arrivo", d: "Registrati ora e sarai tra i primi a sapere quando partirà Readoo Punti." },
  { icon: Newspaper, t: "Un nuovo modo di informarti", d: "Readoo unisce informazione, partecipazione e vantaggi in un’unica esperienza." },
];

export function WhyNow() {
  return (
    <Wrap testid="vantaggi-why" className="pb-14 sm:pb-20">
      <H2>Perché entrare in Readoo già oggi?</H2>
      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
        {WHY.map((w, i) => (
          <div key={w.t} data-testid={`vantaggi-why-${i + 1}`} className="border-t-2 border-brand pt-5">
            <w.icon size={22} className="text-brand" />
            <h3 className="mt-3 font-serif text-xl font-semibold text-onSurface">{w.t}</h3>
            <p className="mt-1.5 text-base leading-relaxed text-onSurfaceSecondary">{w.d}</p>
          </div>
        ))}
      </div>
    </Wrap>
  );
}

export function SignupBlock() {
  const { user } = useAuth();
  const member = !!user;
  return (
    <Wrap testid="vantaggi-signup" className="pb-14 sm:pb-20">
      <div className="relative overflow-hidden rounded-2xl bg-brand text-onBrand p-7 sm:p-12">
        <span aria-hidden className="absolute -right-10 -bottom-10 w-48 h-48 rounded-full bg-white/10" />
        <div className="relative max-w-2xl">
          <h2 className="font-serif text-3xl sm:text-4xl font-semibold leading-tight">{member ? (user.first_name ? `Ciao ${user.first_name}, il tuo Readoo ti aspetta.` : "Il tuo Readoo ti aspetta.") : "Readoo è gratuito per tutti."}</h2>
          <p className="mt-4 text-lg sm:text-xl font-semibold leading-snug">{member ? "Interessi, squadre, meteo, oroscopo e articoli salvati sono nel tuo profilo." : "Ma quando ti registri, diventa il tuo Readoo."}</p>
          <p className="mt-2 text-base opacity-90">{member ? "Sarai tra i primi a scoprire le prossime novità, compreso Readoo Punti." : "Personalizzazione, notizie salvate, vantaggi già attivi e accesso in anteprima alle novità."}</p>
          <Link to={member ? "/profilo" : SIGNUP} data-testid="vantaggi-signup-cta" className="group mt-7 inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-surface text-brand text-sm font-bold uppercase tracking-wide hover:bg-surfaceSecondary transition-colors w-full sm:w-auto">
            {member ? "Vai al tuo Readoo" : "Registrati gratis"} <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
          </Link>
          {!member && <p className="mt-3 text-sm opacity-85">Gratis. Nessun abbonamento richiesto.</p>}
        </div>
      </div>
    </Wrap>
  );
}

export function Finale() {
  return (
    <Wrap testid="vantaggi-finale" className="pb-16 sm:pb-24 text-center">
      <div className="inline-flex items-baseline gap-0.5 mb-4">
        <span className="font-display text-3xl font-bold text-onSurface">Readoo</span>
        <span className="w-2 h-2 rounded-full bg-brand mb-1.5" />
      </div>
      <h2 className="font-serif text-3xl sm:text-4xl font-semibold leading-tight text-onSurface">Registrati. Readoo diventa tuo.</h2>
      <SignupCta testid="vantaggi-finale-cta" label="Registrati gratis" className="mt-7 w-full sm:w-auto" />
    </Wrap>
  );
}
